import type { CSSProperties, FC } from 'react';
import type { TeacherProfile, TeacherWorkspaceSummary } from '../types';
import {
  AcademicCapIcon,
  BookOpenIcon,
  CalendarIcon,
  CheckSquareIcon,
  DocumentTextIcon,
  ExternalLinkIcon,
  FolderIcon,
  LayersIcon,
  MessageSquareIcon,
} from './TeacherIcons';

interface TeacherCabinetGridProps {
  summary: TeacherWorkspaceSummary | null;
  profile?: TeacherProfile | null;
  onOpenCard: (cardId: string) => void;
  onOpenAI: () => void;
  onOpenCreateRequest?: () => void;
}

interface CabinetSectionCard {
  id: string;
  num: string;
  title: string;
  description: string;
  icon: FC<{ className?: string }>;
  accentBorder: string;
  accentText: string;
  badgeBg: string;
}

const SECTION_CARDS: CabinetSectionCard[] = [
  { id: 'connections', num: '04', title: 'Академические связи', description: 'Контакты и переписка с участниками академии.', icon: MessageSquareIcon, accentBorder: 'border-blue-500/30', accentText: 'text-blue-400', badgeBg: 'bg-blue-500/10' },
  {
    id: 'course-workspace',
    num: '01',
    title: 'Учебный процесс преподавателя',
    description: 'Дисциплина → группа → обучающиеся; внутри занятия, задания, проверка, командные проекты, материалы и аналитика.',
    icon: BookOpenIcon,
    accentBorder: 'border-cyan-500/30 hover:border-cyan-400',
    accentText: 'text-cyan-400',
    badgeBg: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30',
  },
  {
    id: 'gradebook',
    num: '02',
    title: 'Электронный журнал',
    description: 'Полный журнал по дисциплинам и группам: недели, посещаемость, лекция, практика, СРО, РК1 / РК2 и итоговый контроль.',
    icon: CheckSquareIcon,
    accentBorder: 'border-emerald-500/30 hover:border-emerald-400',
    accentText: 'text-emerald-400',
    badgeBg: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  },
  {
    id: 'tools',
    num: '03',
    title: 'Программы и инструменты',
    description: 'Опросы, исследовательские инструменты, обработка данных и рабочие сервисы академии.',
    icon: LayersIcon,
    accentBorder: 'border-amber-500/30 hover:border-amber-400',
    accentText: 'text-amber-400',
    badgeBg: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  },
  {
    id: 'scientific-projects',
    num: '04',
    title: 'Научные проекты с обучающимися',
    description: 'Создание научного проекта, добавление обучающихся, роли, этапы, задачи, материалы, консультации и результаты.',
    icon: AcademicCapIcon,
    accentBorder: 'border-purple-500/30 hover:border-purple-400',
    accentText: 'text-purple-400',
    badgeBg: 'bg-purple-500/10 text-purple-300 border-purple-500/30',
  },
  {
    id: 'meetings',
    num: '05',
    title: 'Встречи и приглашения',
    description: 'Создание встреч и приглашений, подтверждение участия, календарь и история.',
    icon: CalendarIcon,
    accentBorder: 'border-rose-500/30 hover:border-rose-400',
    accentText: 'text-rose-400',
    badgeBg: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
  },
  {
    id: 'requests',
    num: '06',
    title: 'Обращения обучающихся',
    description: 'Вопросы по дисциплине, консультации, пересдача, уточнение оценки, дедлайна и другие обращения.',
    icon: MessageSquareIcon,
    accentBorder: 'border-blue-500/30 hover:border-blue-400',
    accentText: 'text-blue-400',
    badgeBg: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
  },
  {
    id: 'attestation',
    num: '07',
    title: 'Аттестация и ведомости',
    description: 'РК1 / РК2 / итоговый контроль, допуск, формирование и фиксация ведомостей.',
    icon: DocumentTextIcon,
    accentBorder: 'border-teal-500/30 hover:border-teal-400',
    accentText: 'text-teal-400',
    badgeBg: 'bg-teal-500/10 text-teal-300 border-teal-500/30',
  },
  {
    id: 'files',
    num: '08',
    title: 'Файлы и документы',
    description: 'Личный рабочий архив преподавателя вне конкретной дисциплины.',
    icon: FolderIcon,
    accentBorder: 'border-sky-500/30 hover:border-sky-400',
    accentText: 'text-sky-400',
    badgeBg: 'bg-sky-500/10 text-sky-300 border-sky-500/30',
  },
  {
    id: 'official-sources',
    num: '09',
    title: 'Официальные источники ENU',
    description: 'Кабинеты, маршруты, ответственные и подтверждённые процедуры ENU; открывает карточку официальных источников.',
    icon: ExternalLinkIcon,
    accentBorder: 'border-indigo-500/30 hover:border-indigo-400',
    accentText: 'text-indigo-400',
    badgeBg: 'bg-indigo-500/10 text-indigo-300 border-indigo-500/30',
  },
];

export const TeacherCabinetGrid: FC<TeacherCabinetGridProps> = ({
  summary,
  profile,
  onOpenCard,
  onOpenAI,
  onOpenCreateRequest,
}) => {
  const currentProfile = profile || summary?.profile;
  const kpi = summary?.kpi;
  const initials =
    currentProfile?.full_name
      ?.split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase() || '—';

  if (!currentProfile || !kpi) {
    return (
      <div className="space-y-6">
        <p className="text-sm text-slate-400">Загрузка кабинета...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="role-hero" style={{ '--role-accent': '#41d2ff' } as CSSProperties}>
        <div className="flex items-center gap-4 min-w-0">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-cyan-600 to-blue-500 text-lg font-black text-white">
            {initials}
          </div>
          <div className="min-w-0">
            <small>
              <span>{currentProfile.academic_id || '—'}</span> · Преподаватель
            </small>
            <h2 className="truncate">{currentProfile.full_name || '—'}</h2>
            <p>
              <span>{currentProfile.department || '—'}</span> · {currentProfile.status || '—'}
            </p>
          </div>
        </div>
        <div className="role-hero-actions">
          <button type="button" className="role-btn" onClick={onOpenAI}>
            Super AI
          </button>
          {onOpenCreateRequest ? (
            <button type="button" className="role-btn primary" onClick={onOpenCreateRequest}>
              + Создать заявку
            </button>
          ) : null}
        </div>
      </div>

      <div className="role-statgrid">
        <button type="button" className="role-stat" style={{ '--a': '#41d2ff' } as CSSProperties} onClick={() => onOpenCard('course-workspace')}>
          <strong>{kpi.disciplines}</strong>
          <span>Дисциплины</span>
        </button>
        <button type="button" className="role-stat" style={{ '--a': '#49ddb7' } as CSSProperties} onClick={() => onOpenCard('course-workspace')}>
          <strong>{kpi.groups}</strong>
          <span>Группы</span>
        </button>
        <button type="button" className="role-stat" style={{ '--a': '#e2ad3a' } as CSSProperties} onClick={() => onOpenCard('course-workspace')}>
          <strong>{kpi.checking}</strong>
          <span>На проверке</span>
        </button>
        <button type="button" className="role-stat" style={{ '--a': '#9e80ff' } as CSSProperties} onClick={() => onOpenCard('meetings')}>
          <strong>{kpi.deadlines}</strong>
          <span>Сроки</span>
        </button>
      </div>

      <div className="owner-hub-top-actions">
        <button type="button" className="owner-hub-ai-action" onClick={onOpenAI}>
          <span className="spark" aria-hidden="true">
            ✦
          </span>
          <span>
            <strong>Super AI</strong>
            <small>Помощник по функциям «Преподаватель» и разрешённым данным</small>
          </span>
          <b>Открыть →</b>
        </button>
        {onOpenCreateRequest ? (
          <button type="button" className="owner-hub-create" onClick={onOpenCreateRequest}>
            + Создать заявку
          </button>
        ) : null}
      </div>

      <div>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">Разделы</h3>
            <p className="text-xs text-slate-400">Навигация по функциональным блокам кабинета преподавателя</p>
          </div>
        </div>

        <div className="owner-hub-grid">
          {SECTION_CARDS.map((card) => {
            const Icon = card.icon;
            const accent = card.accentText.includes('emerald')
              ? '#49ddb7'
              : card.accentText.includes('amber')
                ? '#e2ad3a'
                : card.accentText.includes('purple')
                  ? '#9e80ff'
                  : card.accentText.includes('rose')
                    ? '#ff7a64'
                    : card.accentText.includes('blue')
                      ? '#5da8ff'
                      : card.accentText.includes('teal')
                        ? '#49ddb7'
                        : card.accentText.includes('indigo')
                          ? '#9e80ff'
                          : '#41d2ff';
            return (
              <button
                key={card.id}
                type="button"
                onClick={() => onOpenCard(card.id)}
                className="owner-hub-card"
                style={{ '--action': accent } as CSSProperties}
              >
                <span className="owner-hub-index">{card.num}</span>
                <span className="owner-hub-icon" aria-hidden="true">
                  <Icon className="h-6 w-6" />
                </span>
                <span className="owner-hub-copy">
                  <strong>{card.title}</strong>
                  <span>{card.description}</span>
                </span>
                <span className="owner-hub-arrow" aria-hidden="true">
                  ›
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
